<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/ui/AppIcon.vue'
import type { IconName } from '@/lib/icons'
import { useUiStore } from '@/stores/ui'

export interface NavItem {
  to: string
  labelKey: string
  icon: IconName
  exact?: boolean
  /** Shown, but not a link. Guests see why in a tooltip. */
  locked?: boolean
}

withDefaults(
  defineProps<{
    items: NavItem[]
    footerItems?: NavItem[]
    /** Section label above the links. Those links sit in as children. */
    headingKey?: string
    /** Learner shell: dark petrol active item, teal icons. */
    appearance?: 'default' | 'energy'
  }>(),
  { appearance: 'default' },
)

const { t } = useI18n()
const ui = useUiStore()
</script>

<template>
  <!-- Mobile: an off-canvas drawer. Desktop: always visible. -->
  <div
    v-if="ui.sidebarOpen"
    class="fixed inset-x-0 bottom-0 top-16 z-30 bg-gray-900/40 lg:hidden"
    @click="ui.toggleSidebar(false)"
  />

  <aside
    class="fixed bottom-0 start-0 top-16 z-40 flex w-64 shrink-0 flex-col bg-white transition-transform lg:sticky lg:top-16 lg:h-[calc(100dvh-4rem)] lg:translate-x-0 dark:bg-neutral-800"
    :class="ui.sidebarOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'"
    aria-label="Main"
  >
    <nav class="flex h-full min-h-0 flex-col p-4">
      <p
        v-if="headingKey"
        class="mb-3 px-3 text-[11px] font-medium uppercase tracking-[0.16em] text-neutral-400"
      >
        {{ t(headingKey) }}
      </p>
      <ul class="min-h-0 flex-1 space-y-1 overflow-y-auto">
        <li v-for="item in items" :key="item.to">
          <span
            v-if="item.locked"
            class="flex cursor-default items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-400 dark:text-neutral-500"
            :title="t('nav.signInRequired')"
          >
            <AppIcon :name="item.icon" />
            <span class="min-w-0 flex-1 truncate">{{ t(item.labelKey) }}</span>
            <AppIcon name="lock" size="sm" />
          </span>
          <RouterLink
            v-else
            :to="item.to"
            class="group relative flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-stone-100 dark:hover:bg-neutral-700"
            :class="
              appearance === 'energy'
                ? 'text-reybex-ink hover:text-gray-900 dark:text-neutral-300 dark:hover:text-white'
                : 'text-gray-700 dark:text-neutral-300'
            "
            :active-class="
              appearance === 'energy'
                ? 'bg-reybex-dark text-white hover:bg-stone-100 hover:text-gray-900'
                : 'bg-brand-50 text-brand-800 hover:bg-brand-50 before:absolute before:inset-y-1.5 before:start-0 before:w-0.5 before:rounded-full before:bg-brand-600 dark:bg-brand-950 dark:text-brand-200'
            "
            :exact-active-class="
              item.exact
                ? appearance === 'energy'
                  ? 'bg-reybex-dark text-white'
                  : 'bg-brand-50 text-brand-800 dark:bg-brand-950'
                : ''
            "
            @click="ui.toggleSidebar(false)"
          >
            <AppIcon
              :name="item.icon"
              :class="
                appearance === 'energy'
                  ? 'text-reybex-mint group-[.router-link-active]:!text-white group-hover:!text-gray-900'
                  : ''
              "
            />
            {{ t(item.labelKey) }}
          </RouterLink>
        </li>
      </ul>

      <ul v-if="footerItems?.length" class="space-y-1 border-t border-gray-200 pt-3 dark:border-neutral-700">
        <li v-for="item in footerItems" :key="item.to">
          <RouterLink
            :to="item.to"
            class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-neutral-400 dark:hover:bg-neutral-700"
            @click="ui.toggleSidebar(false)"
          >
            <AppIcon :name="item.icon" />
            {{ t(item.labelKey) }}
          </RouterLink>
        </li>
      </ul>

      <div
        v-if="$slots.footer"
        class="mt-3 shrink-0 border-t border-gray-200 pt-3 dark:border-neutral-700"
      >
        <slot name="footer" />
      </div>
    </nav>
  </aside>
</template>
